import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  QuestionAnswer as QuestionIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { submitContact } from '../api/contactApi';

// ── Contact reasons ────────────────────────────────────────────────────────────

const REASONS = [
  {
    icon: <SchoolIcon />,
    title: 'Learning support',
    body: 'Stuck on a lesson, confused about a concept, or need help setting up your workspace.',
    color: '#3776AB',
  },
  {
    icon: <QuestionIcon />,
    title: 'Platform feedback',
    body: 'Spotted a bug, have a suggestion, or want to tell us what is working well.',
    color: '#336791',
  },
  {
    icon: <EmailIcon />,
    title: 'Everything else',
    body: 'Account issues, content requests, partnerships, or anything else on your mind.',
    color: '#0078D4',
  },
];

const ReasonCard = ({ item }) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderColor: alpha(item.color, 0.25),
        bgcolor: alpha(item.color, 0.03),
        borderRadius: 2,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            bgcolor: alpha(item.color, 0.1),
            color: item.color,
            display: 'flex',
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          {item.icon}
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {item.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
            {item.body}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

// ── Form ───────────────────────────────────────────────────────────────────────

const INITIAL = {
  first_name: '',
  last_name: '',
  email: '',
  subject: '',
  message: '',
};

const ContactPage = () => {
  const theme = useTheme();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState('');

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.first_name.trim()) next.first_name = 'Required';
    if (!form.last_name.trim()) next.last_name = 'Required';
    if (!form.email.trim()) next.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email';
    if (!form.subject.trim()) next.subject = 'Required';
    if (form.message.trim().length < 10) next.message = 'Please write at least 10 characters';
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    setApiError('');
    try {
      await submitContact(form);
      setSubmitted(true);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, sm: 8, md: 12 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={{ xs: 6, sm: 8 }}>
          <Typography
            variant="h2"
            fontWeight={800}
            mb={2}
            sx={{
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3rem' },
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Get in touch
          </Typography>
          <Typography variant="h6" color="text.secondary" fontWeight={400} sx={{ maxWidth: 540, mx: 'auto' }}>
            We are a small team and we read every message. Expect a reply within 1–2 working days.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Left: reasons + email */}
          <Grid item xs={12} md={5}>
            <Stack spacing={2}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                What can we help with?
              </Typography>
              {REASONS.map((r) => <ReasonCard key={r.title} item={r} />)}
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <EmailIcon color="primary" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email us directly</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      help@insitechinternational.com
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Grid>

          {/* Right: form */}
          <Grid item xs={12} md={7}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                borderColor: alpha(theme.palette.primary.main, 0.2),
                bgcolor: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              {submitted ? (
                <Stack spacing={2} alignItems="center" textAlign="center" py={4}>
                  <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main' }} />
                  <Typography variant="h5" fontWeight={700}>Message sent</Typography>
                  <Typography color="text.secondary">
                    Thanks for reaching out. We'll reply to <strong>{form.email}</strong> within
                    1–2 working days.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => { setForm(INITIAL); setSubmitted(false); }}
                    sx={{ mt: 1 }}
                  >
                    Send another message
                  </Button>
                </Stack>
              ) : (
                <>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Send us a message
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Fill in the form and we'll get back to you as soon as we can.
                  </Typography>

                  {apiError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {apiError}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="First name" required
                          value={form.first_name} onChange={set('first_name')}
                          error={Boolean(errors.first_name)} helperText={errors.first_name}
                          disabled={submitting}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="Last name" required
                          value={form.last_name} onChange={set('last_name')}
                          error={Boolean(errors.last_name)} helperText={errors.last_name}
                          disabled={submitting}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth label="Your email" type="email" required
                          value={form.email} onChange={set('email')}
                          error={Boolean(errors.email)} helperText={errors.email}
                          disabled={submitting}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth label="Subject" required
                          value={form.subject} onChange={set('subject')}
                          error={Boolean(errors.subject)} helperText={errors.subject}
                          disabled={submitting}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth label="Message" multiline rows={5} required
                          value={form.message} onChange={set('message')}
                          error={Boolean(errors.message)} helperText={errors.message || `${form.message.length}/5000`}
                          inputProps={{ maxLength: 5000 }}
                          disabled={submitting}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={submitting}
                          sx={{ py: 1.5, fontWeight: 700, textTransform: 'none' }}
                        >
                          {submitting ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <CircularProgress size={18} color="inherit" />
                              <span>Sending…</span>
                            </Stack>
                          ) : 'Send message'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactPage;
